import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, Space, Upload, List } from 'antd';
import { EditOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import { useAssetActions } from '../../../../hooks/useAssets';
import { EditAssetModalProps } from '../../../../types/asset.types';

const { Text } = Typography;

const EditAssetModal: React.FC<EditAssetModalProps> = ({
  visible,
  onClose,
  onSuccess,
  asset
}) => {
  const [form] = Form.useForm();
  const { updateAsset, updateAssetFiles, isLoading } = useAssetActions();
  const [existingFiles, setExistingFiles] = useState<{ path: string; fileName: string; mimeType: string; size: number }[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [hasFileChanges, setHasFileChanges] = useState(false);

  useEffect(() => {
    if (visible && asset) {
      form.setFieldsValue({
        name: asset.name,
        serialNumber: asset.serialNumber
      });
      
      // Mevcut dosyaları set et
      if (asset.files && asset.files.length > 0) {
        setExistingFiles(asset.files.map(file => ({
          path: file.path,
          fileName: file.fileName,
          mimeType: file.mimeType,
          size: file.size
        })));
      } else {
        setExistingFiles([]);
      }
      
      setNewFiles([]);
      setHasFileChanges(false);
    }
  }, [visible, asset, form]);

  const handleSubmit = async (values: any) => {
    if (!asset) return;
    
    try {
      // Eğer dosya değişikliği varsa dosya API'sini kullan
      if (hasFileChanges) {
        await updateAssetFiles(asset.id, existingFiles, newFiles);
      } else {
        // Sadece ad ve seri numarası güncellemesi
        await updateAsset(asset.id, {
          name: values.name,
          serialNumber: values.serialNumber
        });
      }
      
      onSuccess();
      form.resetFields();
      setExistingFiles([]);
      setNewFiles([]);
      setHasFileChanges(false);
    } catch (error) {
      console.error('Update asset failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setExistingFiles([]);
    setNewFiles([]);
    setHasFileChanges(false);
    onClose();
  };

  // Mevcut dosyayı sil
  const handleRemoveExistingFile = (index: number) => {
    const newExistingFiles = existingFiles.filter((_, i) => i !== index);
    setExistingFiles(newExistingFiles);
    setHasFileChanges(true);
  };

  // Yeni dosya ekle
  const handleFileChange = (info: any) => {
    const { fileList } = info;
    const files = fileList.map((file: any) => file.originFileObj).filter(Boolean);
    setNewFiles(files);
    setHasFileChanges(true);
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!asset) return null;

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <div className="bg-info rounded-circle p-2 d-flex align-items-center justify-content-center me-3">
            <EditOutlined className="text-white" style={{ fontSize: '18px' }} />
          </div>
          <div>
            <h5 className="mb-0">Zimmet Bilgilerini Düzenle</h5>
            <Text type="secondary" className="small">Zimmet adı ve seri numarasını güncelle</Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={500}
      footer={null}
      destroyOnClose
    >
      <div className="edit-asset-form">
        {/* Mevcut Bilgiler */}
        <Alert
          message={
            <div>
              <div className="d-flex align-items-center mb-2">
                <i className="ti ti-archive me-2 text-info"></i>
                <strong>Mevcut Bilgiler</strong>
              </div>
              <div className="row">
                <div className="col-6">
                  <Text type="secondary">Ad: </Text>
                  <Text strong>{asset.name}</Text>
                </div>
                <div className="col-6">
                  <Text type="secondary">Seri No: </Text>
                  <Text strong>{asset.serialNumber}</Text>
                </div>
              </div>
            </div>
          }
          type="info"
          showIcon={false}
          className="mb-4"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Zimmet Adı"
            rules={[
              { required: true, message: 'Zimmet adı gereklidir' },
              { min: 2, message: 'Zimmet adı en az 2 karakter olmalıdır' },
              { max: 100, message: 'Zimmet adı en fazla 100 karakter olmalıdır' }
            ]}
          >
            <Input 
              placeholder="Örn: MacBook Pro M3 Max" 
              size="large"
              prefix={<i className="ti ti-archive text-muted"></i>}
            />
          </Form.Item>

          <Form.Item
            name="serialNumber"
            label="Seri Numarası"
            rules={[
              { required: true, message: 'Seri numarası gereklidir' },
              { min: 3, message: 'Seri numarası en az 3 karakter olmalıdır' },
              { max: 50, message: 'Seri numarası en fazla 50 karakter olmalıdır' }
            ]}
          >
            <Input 
              placeholder="Örn: C02XYZ123456" 
              size="large"
              prefix={<i className="ti ti-hash text-muted"></i>}
            />
          </Form.Item>

          {/* Dosya Yönetimi */}
          <div className="mb-4">
            <h6 className="mb-3">Dosyalar</h6>
            
            {/* Mevcut Dosyalar */}
            {existingFiles.length > 0 && (
              <div className="mb-3">
                <Text type="secondary" className="small">Mevcut Dosyalar:</Text>
                <List
                  size="small"
                  dataSource={existingFiles}
                  renderItem={(file, index) => (
                    <List.Item
                      actions={[
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveExistingFile(index)}
                        />
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<FileOutlined />}
                        title={file.fileName}
                        description={`${formatFileSize(file.size)} • ${file.mimeType}`}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Yeni Dosya Yükleme */}
            <Upload
              multiple
              beforeUpload={() => false} // Otomatik yükleme engelle
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
              maxCount={5}
            >
              <Button icon={<UploadOutlined />}>
                Yeni Dosya Ekle (Max 5)
              </Button>
            </Upload>
            
            {newFiles.length > 0 && (
              <div className="mt-2">
                <Text type="secondary" className="small">
                  Eklenecek: {newFiles.map(f => f.name).join(', ')}
                </Text>
              </div>
            )}
          </div>

          <div className="alert alert-info d-flex align-items-center mb-3">
            <i className="ti ti-info-circle me-2"></i>
            <div>
              <strong>Bilgi:</strong> Bu işlem zimmet adı, seri numarası ve dosyalarını günceller. 
              Durum değişikliği veya atama işlemleri için diğer seçenekleri kullanın.
            </div>
          </div>

          <Form.Item className="mb-0">
            <div className="d-flex justify-content-end gap-2">
              <Button onClick={handleCancel} size="large">
                İptal
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isLoading}
                size="large"
                icon={<SaveOutlined />}
              >
                Değişiklikleri Kaydet
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>

      <style>{`
        .edit-asset-form .ant-form-item-label > label {
          font-weight: 600;
        }
        
        .edit-asset-form .ant-input-prefix {
          margin-right: 8px;
        }
      `}</style>
    </Modal>
  );
};

export default EditAssetModal;
